import React, { useEffect, useRef } from "react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import {
  Filter as FilterIcon,
  Search,
  X as XIcon,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import clsx from "clsx";
import { Label } from "../../components/ui/label";
import { useIntl, FormattedMessage } from "react-intl";

function DashboardFilters({
  show,
  onToggle,
  filter,
  setFilter,
  onSearch,
  onClear,
  loading,
}) {
  const intl = useIntl();
  const dropdownRef = useRef(null);

  const hasActiveFilters = Boolean(
    filter.query || filter.startDate || filter.endDate
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    onSearch();
  };

  const handleClear = (field) => {
    setFilter({
      ...filter,
      [field]: "",
    });
    onClear();
  };

  const handleFilterChange = (field, value) => {
    setFilter({
      ...filter,
      [field]: value,
    });
  };

  // 👇 Close when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onToggle(false);
      }
    }

    if (show) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [show, onToggle]);

  return (
    <div className="py-2 mt-2" ref={dropdownRef}>
      <div className="relative inline-block text-left">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => onToggle(!show)}
          className={clsx(
            "flex items-center gap-2 transition-colors",
            hasActiveFilters &&
              "border-blue-600 text-blue-600 bg-blue-50 hover:bg-blue-100 dark:bg-gray-900 dark:text-white"
          )}
          aria-expanded={show}
          aria-label="Toggle filters"
        >
          <FilterIcon className="w-4 h-4" />
          <span>
            <FormattedMessage id="Dashboard.Filters" />
          </span>
          {hasActiveFilters && (
            <span className="ml-1 px-2 py-0.5 text-xs bg-blue-600 text-white rounded-full">
              {
                [filter.query, filter.startDate, filter.endDate].filter(Boolean)
                  .length
              }
            </span>
          )}
          {show ? (
            <ChevronUp className="w-4 h-4 ml-1" />
          ) : (
            <ChevronDown className="w-4 h-4 ml-1" />
          )}
        </Button>

        {show && (
          <form
            onSubmit={handleSubmit}
            className={clsx(
              "absolute right-0 mt-2 w-80 p-4 rounded-lg border shadow-lg z-50",
              hasActiveFilters
                ? "bg-blue-50 border-blue-300 dark:bg-gray-900 dark:border-gray-700"
                : "bg-white border-gray-300 dark:bg-gray-900 dark:border-gray-700"
            )}
          >
            <div className="mb-4">
              <Label>
                <FormattedMessage id="Dashboard.Search" />
              </Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  className="pl-9"
                  placeholder={intl.formatMessage({
                    id: "Dashboard.Plceholder",
                  })}
                  value={filter.query || ""}
                  onChange={(e) => handleFilterChange("query", e.target.value)}
                  autoComplete="off"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <Label>
                  <FormattedMessage id="Dashboard.FromDate" />
                </Label>
                <Input
                  type="date"
                  value={filter.startDate || ""}
                  onChange={(e) =>
                    handleFilterChange("startDate", e.target.value)
                  }
                  max={filter.endDate || undefined}
                />
              </div>

              <div>
                <Label>
                  <FormattedMessage id="Dashboard.ToDate" />
                </Label>
                <Input
                  type="date"
                  value={filter.endDate || ""}
                  onChange={(e) =>
                    handleFilterChange("endDate", e.target.value)
                  }
                  min={filter.startDate || undefined}
                />
              </div>
            </div>

            <div className="flex justify-end space-x-2">
              {hasActiveFilters && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={handleClear}
                  disabled={loading}
                  aria-label="Clear all filters"
                >
                  <XIcon className="w-4 h-4 mr-1" />
                  <FormattedMessage id="Dashboard.Clear" />
                </Button>
              )}

              <Button
                type="submit"
                size="sm"
                disabled={loading}
                aria-label="Apply filters"
                className="min-w-[80px]"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>
                      <FormattedMessage id="Dashboard.Searching..." />
                    </span>
                  </div>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-1" />
                    <FormattedMessage id="Dashboard.Search" />
                  </>
                )}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default DashboardFilters;
